import cn from 'classnames'
import {
  Children,
  memo,
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Translation } from 'cng-common/features/translations'

import { isItem } from './helpers'
import {
  ISegmentedControlItemProps,
  TMultiSegmentedControlProps,
  TSegmentedControlProps,
} from './models'

import styles from './SegmentedControl.module.scss'

const SegmentedControlItem = memo<PropsWithChildren<ISegmentedControlItemProps>>(({ children }) => {
  return (
    <span>{typeof children === 'string' ? <Translation text={children} span /> : children}</span>
  )
})

type SegmentedControlComponent = (<TKey extends React.Key = React.Key>(
  props: TSegmentedControlProps<TKey>
) => React.ReactElement | null) & {
  Item: typeof SegmentedControlItem
  displayName?: string
}

type MultiSegmentedControlComponent = (<TKey extends React.Key = React.Key>(
  props: TMultiSegmentedControlProps<TKey>
) => React.ReactElement | null) & {
  Item: typeof SegmentedControlItem
  displayName?: string
}

const collectItems = (children: React.ReactNode) => {
  const result: React.ReactElement<ISegmentedControlItemProps>[] = []

  Children.forEach(children, (child) => {
    if (isItem(child)) {
      result.push(child)
    }
  })

  return result
}

const resolveItemKey = (
  item: React.ReactElement<ISegmentedControlItemProps>,
  index: number
): React.Key => {
  return item.props.itemKey ?? item.key ?? index
}

export const SegmentedControl = memo(
  ({
    children,
    selectedItem = 0,
    backgroudn = 'bg-default',
    variant = 'default',
    size = 'medium',
    rounded = 'rounded',
    hasBorder = false,
    noWrap = false,
    isDisabled,
    isStreatched,
    useItemKeys,
    onItemSelect,
  }: TSegmentedControlProps<React.Key>) => {
    const items = useMemo(() => collectItems(children), [children])
    const itemKeys = useMemo(() => items.map((item, index) => resolveItemKey(item, index)), [items])
    const activeIndex = useMemo<number>(() => {
      if (useItemKeys) {
        return itemKeys.indexOf(selectedItem as React.Key)
      }

      return typeof selectedItem === 'number' ? selectedItem : -1
    }, [useItemKeys, itemKeys, selectedItem])
    const selectedKey = useMemo(
      () => (useItemKeys ? (selectedItem as React.Key) : undefined),
      [useItemKeys, selectedItem]
    )

    const segmentedControlRef = useRef<HTMLDivElement>(null)
    const itemsRef = useRef<HTMLButtonElement[]>([])
    const [itemElements, setItemElements] = useState<HTMLButtonElement[]>([])

    const handleOnItemSelect = useCallback(
      (index: number) => {
        if (!onItemSelect) {
          return
        }

        if (useItemKeys) {
          const onSelect = onItemSelect as (key: React.Key) => void
          onSelect(itemKeys[index])
          return
        }

        const onSelect = onItemSelect as (index: number) => void
        onSelect(index)
      },
      [itemKeys, onItemSelect, useItemKeys]
    )

    useEffect(() => {
      setItemElements(itemsRef.current.filter(Boolean))
    }, [items])

    useLayoutEffect(() => {
      const updateIndicator = () => {
        if (
          !isDisabled &&
          activeIndex !== undefined &&
          activeIndex >= 0 &&
          segmentedControlRef.current &&
          itemElements[activeIndex]
        ) {
          const activeItem = itemElements[activeIndex]
          const control = segmentedControlRef.current
          const { offsetLeft, offsetWidth } = activeItem

          control.style.setProperty('--indicator-left', `${offsetLeft}px`)
          control.style.setProperty('--indicator-width', `${offsetWidth}px`)
        }
      }

      updateIndicator()

      const observer = new ResizeObserver(() => {
        requestAnimationFrame(updateIndicator)
      })

      itemElements.forEach((item) => {
        if (item) {
          observer.observe(item)
        }
      })

      return () => {
        observer.disconnect()
      }
    }, [activeIndex, itemElements, isDisabled])

    return (
      <div
        className={cn(
          styles.segmentedControl,
          styles[variant],
          styles[rounded],
          styles[backgroudn],
          {
            [styles.border]: hasBorder,
            [styles.noWrap]: noWrap,
            [styles.stretch]: isStreatched,
          }
        )}
        ref={segmentedControlRef}
      >
        {items.map((item, i) => {
          const itemKey = itemKeys[i]

          return (
            <button
              key={itemKey ?? i}
              disabled={item.props.isDisabled || isDisabled}
              className={cn(styles.segmentedControlItem, styles[size], styles[rounded], {
                [styles.stretchItem]: isStreatched,
              })}
              onClick={() => {
                handleOnItemSelect(i)
              }}
              data-selected={useItemKeys ? itemKey === selectedKey : selectedItem === i}
              ref={(el) => {
                if (el) {
                  itemsRef.current[i] = el
                }
              }}
            >
              {item}
            </button>
          )
        })}
      </div>
    )
  }
) as unknown as SegmentedControlComponent

export const MultiSegmentedControl = memo(
  ({
    children,
    selectedItems = [],
    backgroudn = 'bg-default',
    variant = 'default',
    size = 'medium',
    rounded = 'rounded',
    hasBorder = false,
    noWrap = false,
    isDisabled,
    useItemKeys,
    onMultiItemSelect,
  }: TMultiSegmentedControlProps<React.Key>) => {
    const items = useMemo(() => collectItems(children), [children])
    const itemKeys = useMemo(() => items.map((item, index) => resolveItemKey(item, index)), [items])
    const selectedKeys = useMemo(
      () => (useItemKeys ? (selectedItems as React.Key[]) : undefined),
      [useItemKeys, selectedItems]
    )
    const selectedIndexes = useMemo(
      () => (!useItemKeys ? (selectedItems as number[]) : undefined),
      [useItemKeys, selectedItems]
    )

    const handleOnItemSelect = useCallback(
      (index: number) => {
        if (useItemKeys) {
          const key = itemKeys[index]
          const currentKeys = selectedKeys ?? []
          const onSelect = onMultiItemSelect as (keys: React.Key[]) => void
          if (currentKeys.includes(key)) {
            onSelect(currentKeys.filter((el) => el !== key))
          } else {
            onSelect([...currentKeys, key])
          }
          return
        }

        const currentIndexes = selectedIndexes ?? []
        const onSelect = onMultiItemSelect as (indexes: number[]) => void
        if (currentIndexes.includes(index)) {
          onSelect(currentIndexes.filter((el) => el !== index))
        } else {
          onSelect([...currentIndexes, index])
        }
      },
      [itemKeys, onMultiItemSelect, selectedIndexes, selectedKeys, useItemKeys]
    )

    return (
      <div
        className={cn(
          styles.multiSegmentedControl,
          styles[variant],
          styles[rounded],
          styles[backgroudn],
          {
            [styles.border]: hasBorder,
            [styles.noWrap]: noWrap,
          }
        )}
      >
        {items.map((item, i) => {
          const itemKey = itemKeys[i]

          return (
            <button
              disabled={item.props.isDisabled || isDisabled}
              key={itemKey ?? i}
              className={cn(
                styles.segmentedControlItem,
                styles.multiselect,
                styles[size],
                styles[rounded]
              )}
              onClick={() => {
                handleOnItemSelect(i)
              }}
              data-selected={
                useItemKeys ? selectedKeys?.includes(itemKey) : selectedIndexes?.includes(i)
              }
            >
              {item}
            </button>
          )
        })}
      </div>
    )
  }
) as unknown as MultiSegmentedControlComponent

SegmentedControl.displayName = 'SegmentedControl'
SegmentedControlItem.displayName = 'SegmentedControlItem'
MultiSegmentedControl.displayName = 'MultiSegmentedControl'

SegmentedControl.Item = SegmentedControlItem
MultiSegmentedControl.Item = SegmentedControlItem
